import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import {PopoverBody} from 'reactstrap';

import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {OverlayProps, PopoverForm, PopoverHeading} from 'app/general/overlays';
import {PRINT_API} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {pollUntil, postJson} from 'app/utils/request';

export type PrintableContentTypes =
  | 'procedureversion'
  | 'policyversion'
  | 'taskinstance'
  | 'processversion';

type PrintableFormats = 'pdf' | 'docx' | 'html' | 'zip';

type Props = {
  overlayProps: OverlayProps;
  contentType: PrintableContentTypes;
  objectId: number;
  addToPayload?:
    | {
        taskinstance: string;
      }
    | {signoff_id: number};
};

export function PrintDocumentPopover({
  overlayProps,
  contentType,
  objectId,
  addToPayload,
}: Props): JSX.Element {
  const [docType, setDocType] = useState<PrintableFormats>('pdf');

  const [status, setStatus] = useState<
    | 'choiceMenu'
    | 'loading'
    | 'forbidden'
    | 'error'
    | 'success'
    | 'diagramError'
  >('choiceMenu');
  const [downloadURL, setDownloadURL] = useState<string | undefined>(undefined);

  const [meta, setMeta] = useState<{
    attachDiagram: boolean;
    isIncludeMeta: boolean;
    isShortForm: boolean;
  }>({
    attachDiagram: false,
    isIncludeMeta: false,
    isShortForm: false,
  });

  const labels = useMemo(
    () => ({
      pdf: 'PDF',
      docx: 'Word',
      html: 'HTML',
    }),
    []
  );

  const typeLabel = labels[docType];

  const pollPromise = useRef<Promise<unknown>>();

  const cancelPolling = () => {
    // stop polling when the component unmount
    const promise = pollPromise.current;
    // @ts-expect-error typescript doesn't know about Bluebird promise addons
    if (promise != null && !promise.isCancelled()) {
      // @ts-expect-error yes Typescript, Bluebird allows to cancel promises, cool right?
      pollPromise.current.cancel();
    }
  };

  useEffect(() => cancelPolling, []);

  useEffect(() => {
    cancelPolling();
    setStatus('choiceMenu');
  }, [overlayProps.isOpen]);

  const generateDocument = (): void => {
    setStatus('loading');

    pollPromise.current = pollUntil(
      () =>
        postJson(PRINT_API, {
          object_id: objectId,
          content_type: contentType,
          doctype: docType,

          attach_diagram: meta.attachDiagram,
          is_include_meta: meta.isIncludeMeta,
          is_short_form: meta.isShortForm,

          ...(addToPayload || {}),
        }),
      (response) =>
        response.download_url != null || response.render_failed_at != null
    ).then(
      (response) => {
        if (response.render_failed_at != null) {
          if (
            response.additional_information != null &&
            response.additional_information === 'diagram generation failed'
          ) {
            setStatus('diagramError');
          } else {
            setStatus('error');
          }
        } else {
          setStatus('success');
          setDownloadURL(response.download_url);
        }
      },
      (error) => {
        if (error.response && error.response.status === 403) {
          setStatus('forbidden');
        } else {
          setStatus('error');
        }
      }
    );
  };

  const toggleAttachDiagram = (): void => {
    setMeta({...meta, attachDiagram: !meta.attachDiagram});
  };

  const toggleIncludeMeta = (): void => {
    setMeta({...meta, isIncludeMeta: !meta.isIncludeMeta});
  };

  const toggleShortForm = (): void => {
    setMeta({...meta, isShortForm: !meta.isShortForm});
  };

  const closeAndResetStatus = (close: () => unknown) => {
    cancelPolling();
    setStatus('choiceMenu');
    close();
  };

  return (
    <PopoverForm overlayProps={overlayProps}>
      {({close}) => (
        <Fragment>
          <PopoverHeading>{gt('Export')}</PopoverHeading>
          <PopoverBody style={{minWidth: 250}}>
            {status === 'choiceMenu' ? (
              <div>
                {' '}
                <div>
                  <div>{gt('Document format:')}</div>
                  <div className="my-1">
                    <DocTypeRadio
                      value="pdf"
                      label={labels['pdf']}
                      icon="far fa-file-pdf"
                      checked={docType === 'pdf'}
                      onCheck={setDocType}
                    />
                    <DocTypeRadio
                      value="docx"
                      label={labels['docx']}
                      icon="far fa-file-word"
                      checked={docType === 'docx'}
                      onCheck={setDocType}
                    />
                    <DocTypeRadio
                      value="html"
                      label={labels['html']}
                      icon="far fa-file-code"
                      checked={docType === 'html'}
                      onCheck={setDocType}
                    />
                  </div>
                </div>
                {contentType === 'procedureversion' ||
                contentType === 'processversion' ? (
                  <div className="form-check mt-3">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        value=""
                        checked={meta.attachDiagram}
                        onChange={toggleAttachDiagram}
                      />
                      {gt('Attach diagram')}
                    </label>
                  </div>
                ) : null}
                {contentType === 'procedureversion' ? (
                  <div className="form-check mt-1">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        value=""
                        checked={meta.isShortForm}
                        onChange={toggleShortForm}
                      />
                      {gt('Print summary only')}
                    </label>
                  </div>
                ) : null}
                {contentType !== 'taskinstance' ? (
                  <div className="form-check mt-1">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        value=""
                        checked={meta.isIncludeMeta}
                        onChange={toggleIncludeMeta}
                      />
                      {gt('Print approver name')}
                    </label>
                  </div>
                ) : null}
              </div>
            ) : status === 'loading' ? (
              <div>
                <p>
                  {gt(
                    'This could take some seconds, your %s document will appear here when finished',
                    typeLabel
                  )}
                  .
                </p>
                <p className="text-center">
                  <b>
                    <i className="fa fa-circle-notch fa-spin" />{' '}
                    {gt('Exporting %s document', typeLabel)}
                    ...
                  </b>
                </p>
              </div>
            ) : status === 'success' ? (
              <div>
                <p>{gt('Your %s document has been created', typeLabel)}.</p>
                <p className="text-center">
                  <a className="btn btn-primary" href={downloadURL}>
                    {gt('Download %s document', typeLabel)}
                  </a>
                </p>
              </div>
            ) : status === 'error' ? (
              <div className="text-danger">
                <p>
                  {gt('There was an error creating the %s document', typeLabel)}
                  .
                </p>
                <p>{gt('Please try again later')}.</p>
              </div>
            ) : status === 'diagramError' ? (
              <div className="text-danger">
                <p>
                  {gt('There was an error creating the %s document', typeLabel)}
                  .
                </p>
                <p>
                  {gt(
                    'Diagram has not been generated. Contact support if the problem persists'
                  )}
                  .
                </p>
                <p>
                  {gt(
                    'You can still generate the pdf without diagram attachment'
                  )}
                  .
                </p>
              </div>
            ) : status === 'forbidden' ? (
              <div className="text-danger">
                <p>
                  {gt(
                    'You are not allowed to export this %s document',
                    typeLabel
                  )}
                  .
                </p>
                <p>{gt('Please contact the account manager')}.</p>
              </div>
            ) : null}
          </PopoverBody>
          <StyledPopoverBottomDIV className="d-flex">
            <button
              onClick={() => closeAndResetStatus(close)}
              className="btn btn-link btn-sm mr-auto"
            >
              <i className="fa fa-times" />{' '}
              {status === 'loading' ? gt('Cancel') : gt('Close')}
            </button>
            {status === 'choiceMenu' ? (
              <button
                onClick={generateDocument}
                className="btn btn-primary btn-sm ml-auto"
              >
                <i className="fa fa-check" /> {gt('Export')}
              </button>
            ) : null}
          </StyledPopoverBottomDIV>
        </Fragment>
      )}
    </PopoverForm>
  );
}

interface DocTypeRadioProps {
  value: PrintableFormats;
  icon: string;
  label: string;
  checked: boolean;
  onCheck: (value: PrintableFormats) => unknown;
}

function DocTypeRadio({
  value,
  icon,
  label,
  checked,
  onCheck,
}: DocTypeRadioProps) {
  return (
    <label
      className="d-flex my-1 align-items-center py-1 px-2 rounded"
      style={{
        background: colors.lightGray,
      }}
    >
      <input
        type="radio"
        name="docType"
        value={value}
        checked={checked}
        onChange={() => onCheck(value)}
      />
      <span className="ml-2">
        <i className={icon} /> {label}
      </span>
    </label>
  );
}
